import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import Select from "../../../custom/select/select";
import ArrowIcon from "../../../../assets/icons/chevron.svg";
import { formatReportData } from "../../../../utils/formatReportData";
import { PhotoGalleryThumbnail } from "../adminSections/PhotoGalleryThumbnail";
import styles from "../../../headerBtn/headerBtn.module.scss";
import { ReactComponent as DragIcon } from "../../../../assets/icons/drag.svg";
import Check from "../../../../assets/icons/check.svg";

const skuGroupsOptions = [
  { value: "category", label: "Категория" },
  { value: "subCategory", label: "Подкатегория" },
  { value: "group", label: "Группа" },
  { value: "subGroup", label: "Подгруппа" },
];

interface ISkusOptions {
  brand?: string;
  category?: string;
  equals?: { [key: string]: string }[];
  face?: number;
  group?: string;
  position: number;
  sku: { id: number; name: string };
  url: {
    link: string;
    thumb: string;
  };
  stockAvailabilityType?: string | { [key: string]: string } | undefined;
  stockAvailability?: number;
  changes?: { [key: string]: string }[];
  price?: number | undefined;
  hotline?: string;
  aprice?: number;
  amountVirtualRest?: number;
  signal?: {
    isVirtualRestRiskOsa: boolean;
    barcode?: string;
    productCode?: string;
    group?: string;
    productName?: string;
    isAvailable?: boolean;
    isAvailableOsa?: boolean;
    isVirtualRestRisk?: boolean;
    rest?: number;
    akcPrice?: number;
    priceListType?: { [key: string]: string };
    medias?: any[];
  };
  medias?: {
    id: number;
    type: any;
    url: any;
  }[];
}

interface ISkusTableOptions {
  setSelectedSkusPhoto: (el: string) => void;
  previousReportIdRef: React.MutableRefObject<string | null>;
}

interface IColumn {
  id: string;
  label: string;
  visible: boolean;
}

const CustomerSkusTableSection: FC<ISkusTableOptions> = ({
  setSelectedSkusPhoto,
  previousReportIdRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { report, aggregate, adminSettings } = useSelector(
    (state: any) => state.userSingleReport.userSingleReport
  );
  const { darkTheme } = useSelector((state: any) => state.theme.theme);
  const [, setOpenEditIndices] = useState({
    pricesAuction: -1,
    virtualRest: -1,
    hotline: -1,
    price: -1,
    goods: -1,
    face: -1,
  });
  const [isVisibleData, setIsVisibleData] = useState<boolean>(true);
  const [isHasSignal, setIsHasSignal] = useState<boolean>(false);
  const [, setIsHiddenPriceAuction] = useState<boolean>(false);
  const [, setIsHiddenPrice] = useState<boolean>(false);
  const [isHiddenStock, setIsHiddenStock] = useState<boolean>(false);
  const [, setIsHiddenFace] = useState<boolean>(false);
  const [columns, setColumns] = useState<IColumn[]>([]);
  const [tempColumns, setTempColumns] = useState<IColumn[]>([]);
  const [showColumnModal, setShowColumnModal] = useState<boolean>(false);
  const [columnsLoaded, setColumnsLoaded] = useState(false);
  const [isDataSelect, setIsDataSelect] = useState<{
    value: string;
    label: string;
  }>({
    value: "group",
    label: "Группа",
  });
  const [selectedIndexPhoto, setSelectedIndexPhoto] = useState<{
    [key: string]: number;
  } | null>(null);
  const [selectedGalleryPhotos, setSelectedGalleryPhotos] = useState<
    Array<{
      name: string;
      id: number;
      link: string;
    }>
  >([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    localStorage.removeItem("skuColumns");
    setColumns(getDefaultColumns());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (report?.skus) {
      const checkPropertyUndefined = (property: string) =>
        report.skus.every((obj: any) => typeof obj[property] === "undefined");
      setIsHiddenStock(checkPropertyUndefined("stockAvailability"));
      setIsHiddenPrice(checkPropertyUndefined("price"));
      setIsHiddenFace(checkPropertyUndefined("face"));
      setIsHiddenPriceAuction(checkPropertyUndefined("aprice"));
      const checkPropertySignal = report.skus.some((sku: any) =>
        sku.hasOwnProperty("signal")
      );
      setIsHasSignal(checkPropertySignal);
    }
  }, [report]);

  const hasDataForColumn = useCallback(
    (colId: string): boolean => {
      if (!report?.skus) return false;
      switch (colId) {
        case "barcode":
          return report.skus.some(
            (sku: any) => sku.signal && sku.signal.barcode
          );
        case "plu":
          return report.skus.some(
            (sku: any) => sku.signal && sku.signal.productCode
          );
        case "osaGroup":
          return report.skus.some((sku: any) => sku.signal && sku.signal.group);
        case "osaName":
          return report.skus.some(
            (sku: any) => sku.signal && sku.signal.productName
          );
        case "osaSignal":
          return report.skus.some(
            (sku: any) =>
              sku.signal && sku.signal.hasOwnProperty("isAvailableOsa")
          );
        case "boSignal":
          return report.skus.some(
            (sku: any) =>
              sku.signal && sku.signal.hasOwnProperty("isVirtualRestRiskOsa")
          );
        case "osaRest":
          return report.skus.some(
            (sku: any) =>
              sku.signal &&
              sku.signal.rest !== undefined &&
              sku.signal.rest !== 0
          );
        case "osaPrice":
          return report.skus.some(
            (sku: any) =>
              sku.signal &&
              sku.signal.akcPrice !== undefined &&
              sku.signal.akcPrice !== 0
          );
        case "osaInOut":
          return report.skus.some(
            (sku: any) =>
              sku.signal &&
              (sku.signal.isAvailableOsa || sku.signal.isVirtualRestRiskOsa)
          );
        case "boDetected":
          return report.skus.some(
            (sku: any) =>
              sku.amountVirtualRest !== undefined &&
              sku.amountVirtualRest !== null
          );
        case "price":
          return report.skus.some(
            (sku: any) => sku.price !== undefined && sku.price !== null
          );
        case "auctionPrice":
          return report.skus.some(
            (sku: any) =>
              sku.aprice !== undefined &&
              sku.aprice !== null);
        case "face":
          return report.skus.some(
            (sku: any) => sku.face !== undefined && sku.face !== null
          );
        case "photoBefore":
          return report.skus.some(
            (sku: any) =>
              sku.medias &&
              sku.medias.some((m: any) => m.category && m.category.SkuBefore)
          );
        case "photoAfter":
          return report.skus.some(
            (sku: any) =>
              sku.medias &&
              sku.medias.some((m: any) => m.category && m.category.SkuAfter)
          );
        case "stock":
          return report.skus.some((sku: any) => {
            if (Array.isArray(sku.stockAvailability)) {
              return sku.stockAvailability.some(
                (item: any) => item !== undefined && item !== null
              );
            }
            return (
              sku.stockAvailability !== undefined &&
              sku.stockAvailability !== null &&
              sku.stockAvailability !== 0
            );
          });
        default:
          return true;
      }
    },
    [report]
  );

  const getDefaultColumns = useCallback((): IColumn[] => {
    return [
      { id: "pp", label: "п/п", visible: true },
      {
        id: "barcode",
        label: "Штрихкод",
        visible: isHasSignal ? true : hasDataForColumn("barcode"),
      },
      {
        id: "plu",
        label: "PLU",
        visible: isHasSignal ? true : hasDataForColumn("plu"),
      },
      {
        id: "osaGroup",
        label: "Osa_Группа",
        visible: isHasSignal ? true : hasDataForColumn("osaGroup"),
      },
      {
        id: "osaName",
        label: "Osa_Название",
        visible: isHasSignal ? true : hasDataForColumn("osaName"),
      },
      { id: "category", label: "Категория", visible: true },
      { id: "group", label: "Группа", visible: true },
      { id: "name", label: "Наименование", visible: true },
      {
        id: "osaSignal",
        label: "OSA_Сигнал",
        visible: isHasSignal ? true : hasDataForColumn("osaSignal"),
      },
      {
        id: "boSignal",
        label: "BO_Сигнал",
        visible: isHasSignal ? true : hasDataForColumn("boSignal"),
      },
      {
        id: "osaRest",
        label: "OSA Остаток",
        visible: isHasSignal ? true : hasDataForColumn("osaRest"),
      },
      {
        id: "osaPrice",
        label: "OSA_Цена",
        visible: isHasSignal ? true : hasDataForColumn("osaPrice"),
      },
      {
        id: "osaInOut",
        label: "OSA Ин-аут",
        visible: isHasSignal ? true : hasDataForColumn("osaInOut"),
      },
      {
        id: "stock",
        label: "Наличие товара",
        visible: isHasSignal ? true : hasDataForColumn("stock"),
      },
      {
        id: "boDetected",
        label: "Кол-во обнаруженного ВО",
        visible: isHasSignal ? true : hasDataForColumn("boDetected"),
      },
      { id: "price", label: "Цена", visible: hasDataForColumn("price") },
      {id: 'auctionPrice', label: 'Цена акционная', visible: hasDataForColumn('auctionPrice')},
      { id: "face", label: "Фейс", visible: hasDataForColumn("face") },
      { id: "hotline", label: "Горячая линия", visible: true },
      {
        id: "photoBefore",
        label: "Фото до",
        visible: isHasSignal ? true : hasDataForColumn("photoBefore"),
      },
      {
        id: "photoAfter",
        label: "Фото после",
        visible: isHasSignal ? true : hasDataForColumn("photoAfter"),
      },
    ];
  }, [isHasSignal, hasDataForColumn]);

  const filterModalColumns = (cols: IColumn[]): IColumn[] => {
    return cols.filter((col) => {
      if (["price", "auctionPrice", "face"].includes(col.id)) {
        return hasDataForColumn(col.id);
      }
      if (!isHasSignal) {
        const alwaysVisible = ["pp", "category", "group", "name", "hotline"];
        return alwaysVisible.includes(col.id) || hasDataForColumn(col.id);
      }
      return true;
    });
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;
    const modalColumns = filterModalColumns(tempColumns);
    const newModalColumns = Array.from(modalColumns);
    const [removed] = newModalColumns.splice(result.source.index, 1);
    newModalColumns.splice(result.destination.index, 0, removed);
    let modalIndex = 0;
    const newTempColumns = tempColumns.map((col) => {
      return filterModalColumns([col]).length
        ? newModalColumns[modalIndex++]
        : col;
    });
    setTempColumns(newTempColumns);
  };

  const handleOpenColumnModal = () => {
    setTempColumns(JSON.parse(JSON.stringify(columns)));
    setShowColumnModal(true);
  };

  const handleCloseColumnModal = () => {
    setShowColumnModal(false);
  };

  const applyColumnChanges = () => {
    setColumns(tempColumns);
    setShowColumnModal(false);
    localStorage.setItem("skuColumns", JSON.stringify(tempColumns));
  };

  const handleResetColumns = () => {
    setTempColumns(JSON.parse(JSON.stringify(getDefaultColumns())));
  };

  useEffect(() => {
    if (!columnsLoaded && report?.skus) {
      const savedData = localStorage.getItem("skuColumns");
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData) as IColumn[];
          if (Array.isArray(parsed) && parsed.length > 0) {
            setColumns(parsed);
            setColumnsLoaded(true);
            return;
          }
        } catch (e) {
          console.warn("Ошибка парсинга колонок из localStorage", e);
        }
      }
      setColumns(getDefaultColumns());
      setColumnsLoaded(true);
    }
  }, [report, isHasSignal, getDefaultColumns, columnsLoaded]);

  useEffect(() => {
    setColumns((prevCols) =>
      prevCols.map((col) => {
        if (col.id === "stock") return { ...col, visible: !isHiddenStock };
        if (["price", "auctionPrice", "face"].includes(col.id)) {
          return { ...col, visible: hasDataForColumn(col.id) };
        }
        return col;
      })
    );
  }, [report, hasDataForColumn, isHiddenStock]);

  useEffect(() => {
    if (isHasSignal) {
      setColumns((prevCols) =>
        prevCols.map((col) => {
          if (
            col.id === "osaInOut" ||
            col.id === "photoBefore" ||
            col.id === "photoAfter" ||
            col.id === "osaGroup"
          ) {
            return { ...col, visible: true };
          }
          return col;
        })
      );
    }
  }, [isHasSignal]);

  const facesSum = aggregate && parseFloat(aggregate.data.facesSumByAll);
  const photoViewerInstance = document.querySelector(".photoviewer-modal");

  useEffect(() => {
    if (report && report?.reportId?.id) {
      if (
        previousReportIdRef.current !== report?.reportId?.id &&
        photoViewerInstance
      ) {
        photoViewerInstance.remove();
      }
    }
  }, [photoViewerInstance, previousReportIdRef, report]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setOpenEditIndices({
          pricesAuction: -1,
          virtualRest: -1,
          hotline: -1,
          price: -1,
          goods: -1,
          face: -1,
        });
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  const updateButtonStates = (
    context: any,
    index: number | undefined,
    totalItems: number
  ) => {
    if (index === undefined) return;
    if (index === 0) {
      context.$prev.selector[0].style.opacity = 0;
      context.$prev.selector[0].disabled = true;
    } else {
      context.$prev.selector[0].style.opacity = 1;
      context.$prev.selector[0].disabled = false;
    }
    if (index === totalItems - 1) {
      context.$next.selector[0].style.opacity = 0;
      context.$next.selector[0].disabled = true;
    } else {
      context.$next.selector[0].style.opacity = 1;
      context.$next.selector[0].disabled = false;
    }
  };

  useEffect(() => {
    const openPhotoViewer = (items: any[], selected: string | null) => {
      if (selected) {
        const index = items.findIndex((item: { src: string; title?: string }) =>
          item?.src?.includes(selected)
        );
        const options = {
          index,
          callbacks: {
            opened: (context: any) => {
              updateButtonStates(context, context.groupIndex, items.length);
            },
            changed: (context: any, idx?: number) => {
              updateButtonStates(context, idx, items.length);
            },
          },
        };
        if (photoViewerInstance) {
          photoViewerInstance.remove();
        }
        // @ts-ignore
        new PhotoViewer(items, options);
      }
    };
    if (selectedIndexPhoto) {
      const name = Object.keys(selectedIndexPhoto).toString();
      const idx = selectedIndexPhoto[name];
      const findPhoto =
        name === "sku"
          ? { url: { link: report.skus[idx].url.link } }
          : report.skus[idx].signal?.medias?.find(
              (el: {
                type: { [key: string]: string };
                url: { [key: string]: string };
              }) => el.type.hasOwnProperty(name)
            );
      const selectedPhotos =
        name === "sku"
          ? report.skus.map((el: any) => ({ src: el.url.link }))
          : report.skus.flatMap((el: any) => {
              if (el.signal && el.signal.medias) {
                const foundMedia = el.signal.medias.find(
                  (media: { type: any; url: any }) =>
                    media.type.hasOwnProperty(name)
                );
                return foundMedia
                  ? [{ src: foundMedia.url.link, title: foundMedia?.url?.link }]
                  : [];
              }
              return [];
            });
      openPhotoViewer(selectedPhotos, findPhoto?.url?.link);
      setSelectedIndexPhoto(null);
    }
  }, [selectedIndexPhoto, report, photoViewerInstance]);

  if (!report?.skus) return null;

  const displayedSkus: ISkusOptions[] = report.skus
    .slice()
    .sort((a: any, b: any) => a.position - b.position);

  const groupBy = (key: string) => {
    return report?.skus?.reduce((result: any[], currentItem: any) => {
      const groupKey = currentItem[key];
      const existingGroup = result.find((grp) => grp.key === groupKey);
      if (existingGroup) {
        existingGroup.items.push(currentItem);
      } else {
        result.push({ key: groupKey, items: [currentItem] });
      }
      return result;
    }, []);
  };

  const formatString = (input: string) => {
    const regex = /(\d+%) (\d+) from (\d+)/;
    const match = input && input.match(regex);
    if (match) {
      const percent = match[1];
      const current = match[2];
      const total = match[3];
      return `${percent} (${current} из ${total})`;
    }
    return input;
  };

  const getValue = (obj: any, key: string) => {
    return !obj || key === undefined ? "" : obj[key];
  };
  const getMediumSku = (sku: any, face: any, key: string) => {
    const skuData = getValue(sku, key);
    const total = getValue(face, key);
    const parsedTotal = Number(total);
    const regex = /(\d+%) (\d+) from (\d+)/;
    const match = skuData && skuData.match(regex);
    if (match) {
      const currentCount = Number(match[2]);
      if (!isNaN(parsedTotal) && !isNaN(currentCount) && currentCount !== 0) {
        const result = parsedTotal / currentCount;
        return result % 1 === 0 ? result : result.toFixed(2);
      }
    }
    return 0;
  };

  const getObjectBasedOnSelection = () => {
    switch (isDataSelect.value) {
      case "group":
        return {
          sky: aggregate?.data?.skusAvailabilityByGroup,
          face: aggregate?.data?.facesSumByGroup,
        };
      case "subGroup":
        return {
          sky: aggregate?.data?.skusAvailabilityBySubGroup,
          face: aggregate?.data?.facesSumBySubGroup,
        };
      case "category":
        return {
          sky: aggregate?.data?.availabilityByCategory,
          face: aggregate?.data?.facesSumByCategory,
        };
      case "subCategory":
        return {
          sky: aggregate?.data?.availabilityBySubCategory,
          face: aggregate?.data?.facesSumBySubCategory,
        };
      default:
        return null;
    }
  };
  const hasFaceInReport = report?.skus?.some((sku: ISkusOptions) => {
    return sku.face !== null && sku.face !== undefined;
  });
  return (
    <div
      className={`sectionData sectionData_sku ${
        isVisibleData ? "sectionData_active" : ""
      }`}
    >
      <span className="section__title">Данные</span>
      <button
        className="sectionData_button"
        onClick={() => setIsVisibleData(!isVisibleData)}
      >
        <img src={ArrowIcon} alt="Custom Indicator" className="icon" />
      </button>
      {aggregate && adminSettings.includes("skusSummary") && (
        <div className="summaryData">
          <div className="summaryData__filters">
            <p className="summaryData__title">Группировка товара</p>
            <div className="limit__block">
              <div className="error__input_block">
                <Select
                  className="filterSelect"
                  value={isDataSelect}
                  isShowPlaceholder={true}
                  propsChange={(event: { value: string; label: string }) => {
                    groupBy(event.value);
                    setIsDataSelect(event);
                  }}
                  options={skuGroupsOptions}
                  placeholder="Выбрать"
                  name="groups"
                  disabled={false}
                />
              </div>
            </div>
            {aggregate?.data?.availabilityByAll && (
              <p>
                SKU в наличии: {formatString(aggregate.data.availabilityByAll)}
              </p>
            )}{" "}
            {hasFaceInReport && facesSum != null && (
              <p>
                Средний фейс на SKU:{" "}
                {Number.isInteger(facesSum) ? facesSum : facesSum.toFixed(2)}
              </p>
            )}
          </div>
          <div className="summaryData__wrapper customer__skus_table__container">
            <table className="customer__skus_table">
              <thead className="tunder__thead">
                <tr>
                  <th>{isDataSelect.label || "Группа"}</th>
                  <th>SKU в наличии</th>
                  <th>Средний фейс на SKU</th>
                </tr>
              </thead>
              <tbody>
                {groupBy(isDataSelect.value)?.map(
                  (elem: any, index: number) => {
                    const val = getObjectBasedOnSelection();
                    return (
                      <tr key={index}>
                        <td>{elem.key || "Другое"}</td>
                        <td>
                          <span
                            className={
                              formatReportData(elem?.changes)?.includes(
                                "stockAvaliability"
                              )
                                ? "changed__table_value"
                                : ""
                            }
                          >
                            {val?.sky
                              ? formatString(getValue(val.sky, elem.key || ""))
                              : ""}
                          </span>
                        </td>
                        {!isNaN(facesSum) && facesSum !== 0 && (
                          <td>
                            <span
                              className={
                                elem?.changes &&
                                formatReportData(elem?.changes)?.includes(
                                  "face"
                                )
                                  ? "changed__table_value"
                                  : formatReportData(elem?.equals)?.includes(
                                      "Stock"
                                    )
                                  ? "previous__table_value"
                                  : ""
                              }
                            >
                              {val?.face
                                ? getMediumSku(val.sky, val.face, elem.key)
                                : ""}
                            </span>
                          </td>
                        )}
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="summaryData__filters" style={{ marginBottom: "10px" }}>
        <button
          className="column-settings-button"
          onClick={handleOpenColumnModal}
        >
          Настроить колонки
        </button>
      </div>
      {showColumnModal && (
        <div className="modal-overlay">
          <div className="column-modal">
            <h3>Управление колонками</h3>
            <div
              className="select-all-checkbox"
              style={{ marginBottom: "10px" }}
            >
              {(() => {
                const alwaysVisible = [
                  "pp",
                  "category",
                  "group",
                  "name",
                  "hotline",
                ];
                const availableColumns = filterModalColumns(
                  !isHasSignal
                    ? tempColumns.filter(
                        (col) =>
                          alwaysVisible.includes(col.id) ||
                          hasDataForColumn(col.id)
                      )
                    : tempColumns
                );
                return (
                  <label>
                    {" "}
                    <label className={styles.checkbox__container}>
                      {" "}
                      <input
                        checked={availableColumns.every((col) => col.visible)}
                        type="checkbox"
                        onChange={(e) => {
                          const newVisibility = e.target.checked;
                          setTempColumns(
                            tempColumns.map((col) => {
                              if (
                                ["price", "auctionPrice", "face"].includes(
                                  col.id
                                ) &&
                                !hasDataForColumn(col.id)
                              ) {
                                return { ...col, visible: false };
                              }
                              const alwaysVisible = [
                                "pp",
                                "category",
                                "group",
                                "name",
                                "hotline",
                              ];
                              if (
                                !isHasSignal &&
                                !(
                                  alwaysVisible.includes(col.id) ||
                                  hasDataForColumn(col.id)
                                )
                              ) {
                                return { ...col, visible: false };
                              }
                              return { ...col, visible: newVisibility };
                            })
                          );
                        }}
                      />{" "}
                      <span className={styles.multi__reason_checkmark}></span>
                    </label>{" "}
                    {availableColumns.every((col) => col.visible)
                      ? "Снять выбор со всех"
                      : "Выбрать все"}
                  </label>
                );
              })()}
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
              {" "}
              <Droppable droppableId="columns" direction="vertical">
                {(provided) => {
                  const modalColumns = filterModalColumns(
                    !isHasSignal
                      ? tempColumns.filter((column) => {
                          const alwaysVisible = [
                            "pp",
                            "category",
                            "group",
                            "name",
                            "hotline",
                          ];
                          return (
                            alwaysVisible.includes(column.id) ||
                            hasDataForColumn(column.id)
                          );
                        })
                      : tempColumns
                  );
                  return (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="columns-list"
                    >
                      {modalColumns.map((column, index) => (
                        <Draggable
                          key={column.id}
                          draggableId={column.id}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className="column-item"
                            >
                              <label>
                                {" "}
                                <label className={styles.checkbox__container}>
                                  {" "}
                                  <input
                                    checked={column.visible}
                                    type="checkbox"
                                    onChange={() => {
                                      const newCols = tempColumns.map((c) =>
                                        c.id === column.id
                                          ? { ...c, visible: !c.visible }
                                          : c
                                      );
                                      setTempColumns(newCols);
                                    }}
                                  />{" "}
                                  <span
                                    className={styles.multi__reason_checkmark}
                                  ></span>{" "}
                                </label>{" "}
                                {column.label}
                              </label>{" "}
                              <span className="drag-indicator">
                                {" "}
                                <DragIcon
                                  color={
                                    darkTheme.isDarkTheme ? "white" : "black"
                                  }
                                  className={styles.icon}
                                />
                              </span>
                            </div>
                          )}
                        </Draggable>
                      ))}{" "}
                      {provided.placeholder}
                    </div>
                  );
                }}
              </Droppable>{" "}
            </DragDropContext>
            <div className="modal-buttons">
              <button onClick={applyColumnChanges}>Применить</button>
              <button onClick={handleCloseColumnModal}>Отмена</button>
              <button
                onClick={handleResetColumns}
                style={{ marginLeft: "auto" }}
              >
                Сброс
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="customer__skus_table__container">
        <table className="customer__skus_table" style={{ marginTop: "10px" }}>
          <thead className="tunder__thead">
            <tr>
              {columns
                .filter((col) => col.visible)
                .map((col) => (
                  <th key={col.id}>{col.label}</th>
                ))}
            </tr>
          </thead>
          <tbody>
            {displayedSkus.map((sku: ISkusOptions, index: number) => {
              const skuMedias = (report.medias || []).filter((m: any) =>
                sku?.medias?.some((mm: any) => mm.id === m.media.id)
              );
              const hotlineValue = sku.hotline || "";
              const beforePhotos = skuMedias
                .filter((m: any) => m.category?.SkuBefore)
                .map((m: any) => ({
                  id: m.media.id,
                  thumb: m.url.thumb,
                  link: m.url.link,
                  name: sku.sku.name,
                }));
              const afterPhotos = skuMedias
                .filter((m: any) => m.category?.SkuAfter)
                .map((m: any) => ({
                  id: m.media.id,
                  thumb: m.url.thumb,
                  link: m.url.link,
                  name: sku.sku.name,
                }));
              return (
                <tr key={`${sku.sku.id}-${index}`}>
                  {columns
                    .filter((col) => col.visible)
                    .map((col) => {
                      switch (col.id) {
                        case "pp":
                          return <td key={col.id}>{index + 1}</td>;
                        case "barcode":
                          return (
                            <td key={col.id}>{sku.signal?.barcode || ""}</td>
                          );
                        case "plu":
                          return (
                            <td key={col.id}>
                              {sku.signal?.productCode || ""}
                            </td>
                          );
                        case "osaGroup":
                          return (
                            <td className={"table-section"} key={col.id}>
                              {sku.signal?.group || ""}
                            </td>
                          );
                        case "osaName":
                          return (
                            <td className={"table-section"} key={col.id}>
                              {sku.signal?.productName || ""}
                            </td>
                          );
                        case "category":
                          return (
                            <td className={"table-section"} key={col.id}>
                              {sku.category || ""}
                            </td>
                          );
                        case "group":
                          return (
                            <td className={"table-section"} key={col.id}>
                              {sku.group || ""}
                            </td>
                          );
                        case "name":
                          return (
                            <td className={"table-section"} key={col.id}>
                              <span
                                className={sku?.url ? "sku__name_link" : ""}
                                onClick={() =>
                                  sku?.url &&
                                  setSelectedSkusPhoto(sku?.url?.link)
                                }
                              >
                                {sku?.brand || ""} {sku.sku.name}
                              </span>
                            </td>
                          );
                        case "osaSignal":
                          return (
                            <td key={col.id}>
                              {sku.signal?.isAvailableOsa ? (
                                <div className="cellRecord cellRecord_visible previous__table_value_orange" />
                              ) : (
                                ""
                              )}
                            </td>
                          );
                        case "boSignal":
                          return (
                            <td key={col.id}>
                              {sku.signal?.isVirtualRestRiskOsa ? (
                                <div className="cellRecord cellRecord_visible previous__table_value_orange" />
                              ) : (
                                ""
                              )}
                            </td>
                          );
                        case "osaRest":
                          return <td key={col.id}>{sku.signal?.rest ?? ""}</td>;
                        case "osaPrice":
                          return (
                            <td key={col.id}>
                              <span
                                className={`previous__table_value ${
                                  sku.signal?.priceListType?.hasOwnProperty(
                                    "Red"
                                  )
                                    ? "previous__table_value_red"
                                    : sku.signal?.priceListType?.hasOwnProperty(
                                        "Yellow"
                                      )
                                    ? "previous__table_value_yellow"
                                    : "previous__table_value_none"
                                }`}
                              >
                                {sku.signal?.akcPrice || ""}
                              </span>
                            </td>
                          );
                        case "osaInOut":
                          return <td key={col.id}></td>;
                        case "stock":
                          if (
                            sku.stockAvailabilityType &&
                            Object.keys(
                              sku.stockAvailabilityType
                            ).toString() === "bool"
                          ) {
                            return (
                              <td key={col.id}>
                                <input
                                  style={{
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                    width: "20px",
                                    height: "20px",
                                    border: "1px solid #007AFF",
                                    borderRadius: "3px",
                                    backgroundColor: Boolean(
                                      sku.stockAvailability &&
                                        sku.stockAvailability > 0
                                    )
                                      ? "#007AFF"
                                      : "transparent",
                                    backgroundImage: Boolean(
                                      sku.stockAvailability &&
                                        sku.stockAvailability > 0
                                    )
                                      ? `url(${Check})`
                                      : "none",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "center",
                                  }}
                                  type="checkbox"
                                  readOnly
                                  checked={Boolean(
                                    sku?.stockAvailability &&
                                      sku.stockAvailability > 0
                                  )}
                                />
                              </td>
                            );
                          } else {
                            return (
                              <td key={col.id}>
                                <span>
                                  {sku.stockAvailabilityType === "bool" &&
                                  sku.stockAvailability &&
                                  sku.stockAvailability <= 0
                                    ? "Нет"
                                    : sku.stockAvailabilityType === "bool" &&
                                      sku.stockAvailability &&
                                      sku.stockAvailability > 0
                                    ? "Да"
                                    : sku.stockAvailability}
                                </span>
                              </td>
                            );
                          }
                        case "boDetected":
                          return (
                            <td key={col.id}>
                              <span>{sku.amountVirtualRest ?? ""}</span>
                            </td>
                          );
                        case "price":
                          return (
                            <td key={col.id}>
                              <span>
                                {sku.price !== undefined && sku.price !== null
                                  ? sku.price
                                  : ""}
                              </span>
                            </td>
                          );
                        case "auctionPrice":
                          return (
                            <td key={col.id}>
                              <span>{sku.aprice !== null && sku.aprice !== undefined ? sku.aprice : ""}</span>
                            </td>
                          );
                        case "face":
                          return (
                            <td key={col.id}>
                              <span>
                                {sku.face !== undefined && sku.face !== null
                                  ? sku.face
                                  : ""}
                              </span>
                            </td>
                          );
                        case "hotline":
                          return (
                            <td key={col.id} style={{ minWidth: 200 }}>
                              <span>{hotlineValue}</span>
                            </td>
                          );
                        case "photoBefore":
                          return (
                            <td key={col.id} className="photo_td">
                              <PhotoGalleryThumbnail
                                photos={beforePhotos}
                                onView={(photos: any) => {
                                  setSelectedGalleryPhotos(photos);
                                  setCurrentPhotoIndex(0);
                                }}
                                skuId={sku.sku.id}
                                category="SkuBefore"
                              />
                            </td>
                          );
                        case "photoAfter":
                          return (
                            <td key={col.id} className="photo_td">
                              <PhotoGalleryThumbnail
                                photos={afterPhotos}
                                onView={(photos: any) => {
                                  setSelectedGalleryPhotos(photos);
                                  setCurrentPhotoIndex(0);
                                }}
                                skuId={sku.sku.id}
                                category="SkuAfter"
                              />
                            </td>
                          );
                        default:
                          return <td key={col.id} />;
                      }
                    })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {selectedGalleryPhotos.length > 0 && (
        <div className="photo-viewer-modal">
          <div className="photo-viewer-backdrop" />
          <div className="photo-viewer-container">
            <div className="photo-viewer-header">
              <button
                className="close-button"
                onClick={() => {
                  setSelectedGalleryPhotos([]);
                  setCurrentPhotoIndex(0);
                }}
              >
                &times;
              </button>
            </div>
            <div className="photo-viewer-content">
              <div className="photo-title">
                {selectedGalleryPhotos[currentPhotoIndex].name}
              </div>
              <div className="main-photo-container">
                <img
                  src={selectedGalleryPhotos[currentPhotoIndex].link}
                  alt="Full size"
                  className="main-photo"
                />
              </div>
              <div className="photo-controls">
                <button
                  className="nav-button"
                  onClick={() =>
                    setCurrentPhotoIndex((prev) => Math.max(0, prev - 1))
                  }
                  disabled={currentPhotoIndex === 0}
                >
                  &lt;
                </button>
                <div className="thumbnail-strip">
                  {selectedGalleryPhotos.map((photo, i) => (
                    <img
                      key={photo.id}
                      src={photo.link}
                      className={`thumbnail ${
                        i === currentPhotoIndex ? "active" : ""
                      }`}
                      onClick={() => setCurrentPhotoIndex(i)}
                      alt={`Thumbnail ${i + 1}`}
                    />
                  ))}
                </div>
                <button
                  className="nav-button"
                  onClick={() =>
                    setCurrentPhotoIndex((prev) =>
                      Math.min(selectedGalleryPhotos.length - 1, prev + 1)
                    )
                  }
                  disabled={
                    currentPhotoIndex === selectedGalleryPhotos.length - 1
                  }
                >
                  &gt;
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSkusTableSection;
